import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function SelfPortrait({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp } = useOrientation(orientation)

  return (
    <AbsoluteFill>
      <AbsoluteFill>
        <div
          className={cn(
            'w-full',
            p && 'h-[446px]',
            s && 'h-[320px]',
            l && 'h-[343px]',
          )}
          style={{ backgroundColor: style.border.color }}
        />
      </AbsoluteFill>

      <AbsoluteFill>
        <div
          className={cn(
            p && 'ml-[610px]',
            s && 'ml-[446px]',
            l && 'ml-[524px]',
          )}
        >
          <BarsVisualization
            className={cn(p && 'h-[460px]', s && 'h-[360px]', l && 'h-[360px]')}
            color={style.waveform.color}
            gapSize={2}
            lineThickness={8}
            mediaStart={clipScene.mediaStart}
            mediaUrl={sceneAudioSrc(clipScene)}
            waveformUrl={clipScene.waveformUrl}
            placement="under"
          />
        </div>
      </AbsoluteFill>

      {clipScene.showZealousLogo && (
        <ZealousSvg
          color={style.logo.color}
          className="absolute top-[60px] right-[60px] h-[49px] opacity-50"
        />
      )}

      <AbsoluteFill
        className={cn('!flex-row', s && 'p-[50px]', lp && 'p-[70px]')}
      >
        <div className="flex flex-col">
          <div
            className={cn(
              'relative',
              p && 'w-[540px]',
              s && 'w-[406px]',
              l && 'w-[454px]',
            )}
          >
            <FallbackImg
              src={speaker?.avatarUrl}
              className={cn(
                p && 'w-[540px]',
                s && 'w-[406px]',
                l && 'w-[454px]',
              )}
            />

            <p
              className={cn(
                'absolute line-clamp-1 w-full p-2 text-center leading-[137%] font-[700]',
                s && 'text-[30px]',
                lp && 'text-[32px]',
              )}
              style={{
                ...style.speakerUsername,
                backgroundColor: style.border.color,
              }}
            >
              {speaker?.username}
            </p>
          </div>

          {orientation !== 'landscape' && (
            <div className="flex h-full items-center">{children}</div>
          )}

          <div
            className={cn(
              'flex',
              p && 'pt-[70px]',
              s && 'pt-[50px]',
              l && 'h-full flex-col-reverse items-start',
              sp && 'items-center border-t-2',
            )}
            style={{ borderColor: style.border.color }}
          >
            {orientation !== 'landscape' && (
              <FallbackImg
                src={clipScene.imageUrl}
                className={cn(
                  'aspect-square',
                  p && 'mr-[45px] h-[163px]',
                  s && 'mr-[50px] h-[130px]',
                )}
              />
            )}

            <div className="flex flex-col">
              <p
                className={cn(
                  'text-left leading-[150%] font-[600]',
                  s && 'line-clamp-1 text-[30px]',
                  l && 'w-[454px] pr-[70px] pb-[70px]',
                  lp && 'line-clamp-2 text-[36px]',
                )}
                style={style.title}
              >
                {clipScene.title}
              </p>

              <p
                className={cn(
                  'text-left leading-[150%] font-[600]',
                  s && 'line-clamp-1 text-[30px]',
                  l && 'w-[454px] pr-[70px]',
                  lp && 'line-clamp-2 text-[36px]',
                )}
                style={style.subtitle}
              >
                {clipScene.subtitle}
              </p>
            </div>
          </div>
        </div>

        {orientation === 'landscape' && (
          <div className="mt-[343px] ml-[106px] flex w-[60%] shrink-0 items-center">
            {children}
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  )
}

export const selfPortraitLayoutInfo: LayoutInfo = {
  name: 'selfPortrait',
  Component: SelfPortrait,
  defaultScene: {
    ...defaultClipScene,
    layout: 'selfPortrait',
  },
  defaultStyle: {
    background: { color: 'accent' },
    border: { color: 'primary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    subtitle: { color: 'primary', fontFamily: 'primary', opacity: 0.5 },
    title: { color: 'primary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'primary',
      fontFamily: 'primary',
      highlightBgColor: 'primary',
      unPlayedColor: 'primary',
      unPlayedOpacity: 0.5,
      highlightType: 'last-word-bg',
      fontSize: 59,
      fontWeight: 700,
      scrollType: 'page-by-page',
      linesPerPage: 3,
      lineHeight: 1.3,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 76,
      lineHeight: 1.4,
      linesPerPage: 5,
    },
    portrait: {
      fontSize: 77,
      lineHeight: 1.6,
      linesPerPage: 6,
    },
  },
}
